import toast, { Toast } from "react-hot-toast";

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";

import { Button } from "shared/ui/shadcn/button";

/**
 * USAGE
 *   alert({
        type: "success",
        title: "Your campaign has been successfully deleted",
        description:
          "If you would like to reinstate your campaign, please reach out to Glencoco",
      });
 */

export interface AlertDataI {
  type: "success" | "warning" | "error" | "info" | "neutral";
  title: string;
  description?: string;
  duration?: number;
  className?: string;
}

interface AlertContentI extends AlertDataI {
  toastData?: Toast;
}

export const AlertSuccess = ({
  title,
  description,
  className,
}: AlertContentI) => {
  return (
    <div
      className={twMerge(
        clsx("alert alert-success text-neutral mb-2 shadow-lg", className)
      )}
    >
      <div>
        <CheckCircleIcon className="text-success-content h-6 w-11" />

        <div>
          <h3 className="font-bold">{title}</h3>
          {description && (
            <div
              className="text-xs"
              dangerouslySetInnerHTML={{ __html: description as string }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const AlertWarning = ({
  title,
  description,
  className,
}: AlertContentI) => {
  return (
    <div
      className={twMerge(
        clsx("alert alert-warning text-neutral mb-2 shadow-lg", className)
      )}
    >
      <div>
        <ExclamationTriangleIcon className="text-warning-content h-6 w-11" />

        <div>
          <h3 className="font-bold">{title}</h3>

          {description && (
            <div
              className="text-xs"
              dangerouslySetInnerHTML={{ __html: description as string }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const AlertError = ({
  title,
  description,
  className,
}: AlertContentI) => {
  return (
    <div
      className={twMerge(
        clsx("alert alert-error text-neutral mb-2 shadow-lg", className)
      )}
    >
      <div>
        <XCircleIcon className="text-error-content h-6 w-11" />

        <div>
          <h3 className="font-bold">{title}</h3>
          {description && (
            <div
              className="text-xs"
              dangerouslySetInnerHTML={{ __html: description as string }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const AlertInfo = ({ title, description, className }: AlertContentI) => {
  return (
    <div
      className={twMerge(
        clsx("alert alert-info text-neutral mb-2 shadow-lg", className)
      )}
    >
      <div>
        <CheckCircleIcon className="text-info-content h-6 w-6" />

        <div>
          <h3 className="font-bold">{title}</h3>
          {description && (
            <div
              className="text-xs"
              dangerouslySetInnerHTML={{ __html: description as string }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const AlertNeutral = ({
  title,
  description,
  toastData,
  className,
}: AlertContentI) => {
  return (
    <div
      className={twMerge(
        clsx(
          "alert mb-2 w-[360px] bg-white text-[#18181B] shadow-lg",
          className
        )
      )}
    >
      <div className="flex w-full items-center justify-between">
        <div>
          <h3 className="typography-body-4-bold mb-1">{title}</h3>

          {description && (
            <div
              className="typography-body-4"
              dangerouslySetInnerHTML={{ __html: description as string }}
            />
          )}
        </div>

        <Button
          variant="outline"
          onClick={() => {
            toast.remove(toastData?.id);
          }}
        >
          Okay
        </Button>
      </div>
    </div>
  );
};

export const Alert = ({ toastData, ...props }: AlertContentI) => (
  <div
    className={`${
      toastData?.visible ? "animate-enter" : "animate-leave"
    } pointer-events-auto`}
  >
    {props.type === "success" && (
      <AlertSuccess toastData={toastData} {...props} />
    )}

    {props.type === "warning" && (
      <AlertWarning toastData={toastData} {...props} />
    )}

    {props.type === "error" && <AlertError toastData={toastData} {...props} />}

    {props.type === "info" && <AlertInfo toastData={toastData} {...props} />}

    {props.type === "neutral" && (
      <AlertNeutral toastData={toastData} {...props} />
    )}
  </div>
);

const alert = (props: AlertDataI) => {
  toast.custom((t) => <Alert toastData={t} {...props} />, {
    duration: props.duration || 4000,
  });
};

export default alert;
